import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Dashboard, ArrowBack, ExpandMore } from '@mui/icons-material';
import api from '../../services/api';

const karatMap = {
  '22K': '22K',
  '22/22K': '22K',
  '24K': '24K',
  '24/24K': '24K',
  '18K': '18K',
  'PT95': 'PT95',
  'WG': 'WG'
};
const karatOrder = ['22K', '24K', '18K', 'PT95', 'WG'];

const KarigarOverview = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const karigarParam = params.get('karigar') || '';
  
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchKarigars = async () => {
      try {
        const url = karigarParam 
          ? `/api/karigar/overview-summary?karigar=${karigarParam}`
          : '/api/karigar/overview-summary';
        const res = await api.get(url);
        setKarigars(res.data);
      } catch (err) {
        setError('Failed to fetch karigars: ' + (err.response?.data?.message || err.message));
      }
      setLoading(false);
    };
    fetchKarigars();
  }, [karigarParam]);

  const getPerKaratTotals = (obj = {}) => {
    const totals = {};
    Object.entries(obj).forEach(([k, v]) => {
      const mapped = karatMap[k] || k;
      totals[mapped] = (totals[mapped] || 0) + (typeof v === 'number' ? v : parseFloat(v) || 0);
    });
    return totals;
  };

  const renderDetailTable = (title, data, showCount = false) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1, fontSize: '0.75rem' }}>
          No data available
        </Typography>
      );
    }

    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', p: 0.5 }}>Karat</TableCell>
              {showCount && <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', p: 0.5 }}>Count</TableCell>}
              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', p: 0.5 }}>Weight (g)</TableCell>
              {data[Object.keys(data)[0]]?.totalCharge !== undefined && (
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', p: 0.5 }}>Charge</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {karatOrder.map(karat => {
              const karatData = data[karat];
              if (!karatData || (karatData.totalWeight === 0 && karatData.count === 0)) return null;
              
              return (
                <TableRow key={karat}>
                  <TableCell sx={{ fontSize: '0.75rem', p: 0.5 }}>{karat}</TableCell>
                  {showCount && <TableCell align="center" sx={{ fontSize: '0.75rem', p: 0.5 }}>{karatData.count || 0}</TableCell>}
                  <TableCell align="center" sx={{ fontSize: '0.75rem', p: 0.5 }}>
                    {(karatData.totalWeight || karatData || 0).toFixed(2)}
                  </TableCell>
                  {karatData.totalCharge !== undefined && (
                    <TableCell align="center" sx={{ fontSize: '0.75rem', p: 0.5 }}>{karatData.totalCharge.toFixed(2)}</TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100vw',
      minHeight: '100vh',
      background: '#f7f7f7',
      px: 0,
      py: 0,
      overflowX: 'hidden',
      position: 'relative',
    }}>
      {/* Header */}
      <Box sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#fff',
        borderBottom: '2px solid #e0e0e0',
        px: 3,
        py: 2,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Dashboard sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Karigar Overview</Typography>
        </Box>
        <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="contained" color="primary" sx={{ fontWeight: 600 }}>
          Back
        </Button>
      </Box>

      {/* Content */}
      <Box sx={{ px: 3, py: 2 }}>
        {karigars.map((karigar) => {
          const given = getPerKaratTotals(karigar.totalGiven);
          const received = getPerKaratTotals(karigar.totalReceived);
          const metalOutstanding = getPerKaratTotals(karigar.metalOutstanding);

          return (
            <Paper key={karigar.abbreviation} elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
              {/* Karigar Header */}
              <Box sx={{ px: 2, py: 1.5, background: '#1976d2', color: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  {karigar.abbreviation} - {karigar.name}
                </Typography>
              </Box>

              {/* Main Summary Table */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', p: 0.5 }}>Karat</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', p: 0.5 }}>Brought</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', p: 0.5 }}>Sent</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', p: 0.5 }}>Left</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {karatOrder.map(karat => {
                      const givenVal = parseFloat(given[karat]) || 0;
                      const receivedVal = parseFloat(received[karat]) || 0;
                      const bal = givenVal - receivedVal;
                      
                      let color = 'inherit';
                      if (bal < 0) color = '#d32f2f';
                      else if (bal > 0) color = '#388e3c';

                      return (
                        <TableRow key={karat}>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.75rem', p: 0.5 }}>{karat}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', p: 0.5 }}>{givenVal.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.75rem', p: 0.5 }}>{receivedVal.toFixed(2)}</TableCell>
                          <TableCell align="center" sx={{ color, fontWeight: 600, fontSize: '0.75rem', p: 0.5 }}>
                            {bal.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              {/* Detailed Sections */}
              <Box sx={{ p: 1 }}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>1. Metal Issue Outstanding</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 1 }}>
                    {renderDetailTable('Metal Outstanding', metalOutstanding)}
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>2. Work Receipt Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 1 }}>
                    {renderDetailTable('Work Receipt', karigar.workReceiptDetails, true)}
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>3. Wastage Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 1 }}>
                    {renderDetailTable('Wastage', karigar.wastageSummary)}
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>4. Work Outstanding</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 1 }}>
                    {renderDetailTable('Work Outstanding', karigar.workOutstanding, true)}
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>5. Making Charge Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 1 }}>
                    {renderDetailTable('Making Charges', karigar.makingChargeDetails, true)}
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>6. Item Outstanding Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 1 }}>
                    {renderDetailTable('Item Outstanding', karigar.itemOutstanding, true)}
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default KarigarOverview;
